import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  Stack,
  useTheme,
  alpha,
  Button,
} from "@mui/material";
import {
  Support as SupportIcon,
  Security as SecurityIcon,
  Update as UpdateIcon,
  Speed as SpeedIcon,
  MonitorHeart as MonitorIcon,
  Handshake as HandshakeIcon,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

const MAINTENANCE_FEATURES = [
  {
    icon: SupportIcon,
    title: "24/7 Technical Support",
    description:
      "Round-the-clock assistance for critical issues and system maintenance",
    color: "primary",
  },
  {
    icon: SecurityIcon,
    title: "Security Updates",
    description: "Regular security patches and updates to protect your systems",
    color: "error",
  },
  {
    icon: UpdateIcon,
    title: "System Updates",
    description: "Regular feature updates and system improvements",
    color: "info",
  },
  {
    icon: SpeedIcon,
    title: "Performance Optimization",
    description: "Continuous monitoring and optimization for peak performance",
    color: "success",
  },
  {
    icon: MonitorIcon,
    title: "Proactive Monitoring",
    description: "Real-time system monitoring and issue prevention",
    color: "warning",
  },
  {
    icon: HandshakeIcon,
    title: "Service Level Agreement",
    description: "Guaranteed response times and service quality",
    color: "secondary",
  },
];

export default function Maintenance() {
  const theme = useTheme();
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        py: { xs: 4, md: 6 },
        bgcolor: "background.default",
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "100%",
          background: `linear-gradient(180deg, ${alpha(
            theme.palette.primary.main,
            0.04
          )} 0%, transparent 100%)`,
        },
      }}
    >
      <Container>
        <Stack spacing={6}>
          <Stack spacing={2} sx={{ textAlign: "center" }}>
            <Typography variant="h3" sx={{ fontWeight: 700 }}>
              Maintenance & Support
            </Typography>
            <Typography variant="body1" sx={{ color: "text.secondary" }}>
              We provide comprehensive maintenance and technical support to
              ensure your systems run smoothly. Enjoy 6 months of complimentary
              support with all our solutions.
            </Typography>
          </Stack>

          <Grid container spacing={3}>
            {MAINTENANCE_FEATURES.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={feature.title}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card
                    elevation={0}
                    sx={{
                      height: "100%",
                      p: 3,
                      transition: "all 0.3s ease-in-out",
                      border: `1px solid ${alpha(
                        theme.palette[feature.color].main,
                        0.1
                      )}`,
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: theme.shadows[8],
                        borderColor: "transparent",
                        bgcolor: alpha(theme.palette[feature.color].main, 0.02),
                      },
                    }}
                  >
                    <Stack spacing={3}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: 1.5,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: alpha(
                            theme.palette[feature.color].main,
                            0.1
                          ),
                          color: `${feature.color}.main`,
                          transition: "transform 0.3s ease-in-out",
                          ".MuiCard-root:hover &": {
                            transform: "scale(1.1) rotate(5deg)",
                          },
                        }}
                      >
                        <feature.icon sx={{ fontSize: 24 }} />
                      </Box>

                      <Box>
                        <Typography
                          variant="h6"
                          gutterBottom
                          sx={{ fontWeight: 600 }}
                        >
                          {feature.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {feature.description}
                        </Typography>
                      </Box>
                    </Stack>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ textAlign: "center", mt: 4 }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={() => navigate("/contact")}
              sx={{
                px: 4,
                py: 1.5,
                "&:hover": {
                  transform: "translateY(-2px)",
                },
              }}
            >
              Get Support Now
            </Button>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
}
